import {
  classificationAnimals,
  resolveIdentificationKey,
  type AnimalEvidence,
  type KeyOutcome,
} from "../domain/classification";
import type { ChallengeDefinition } from "../domain/schema";
import { shuffleAnswerPresentation } from "./presentationOrder";

interface Options {
  panel: HTMLElement;
  onComplete(challenge: ChallengeDefinition, hintsUsed: number): void;
  onClose(): void;
  speak(text: string): void;
}

const animalIcons: Record<string, string> = {
  salmon: "🐟",
  frog: "🐸",
  gecko: "🦎",
  puffin: "🐦",
  seal: "🦭",
  crab: "🦀",
  ladybird: "🐞",
  earthworm: "🪱",
  octopus: "🐙",
  newt: "◉",
};

export class CoastChallengeController {
  private challenge?: ChallengeDefinition;
  private hintsUsed = 0;
  private completedKeyAnimals = new Set<string>();
  private keyAnimal?: AnimalEvidence;
  private keyNode = "backbone";

  constructor(private readonly options: Options) {}

  open(challenge: ChallengeDefinition): void {
    this.challenge = challenge;
    this.hintsUsed = 0;
    this.completedKeyAnimals.clear();
    this.keyAnimal = undefined;
    this.keyNode = "backbone";
    this.options.panel.hidden = false;
    this.options.panel.innerHTML = `
      <div class="challenge-heading">
        <div><span class="pill">Classification Coast observation post</span><h2 id="challenge-title">${challenge.title}</h2></div>
        <button class="icon-button" data-action="close" type="button" aria-label="Return to the coast">×</button>
      </div>
      <div id="challenge-body" class="challenge-body"></div>
      <div class="hint-row">
        <button class="secondary-button secondary-button--compact" data-action="hint" type="button">Need a hint?</button>
        <p id="hint-text" class="hint-text" aria-live="polite">Read every animal's evidence card. You can always ask for help.</p>
      </div>
      <output id="challenge-feedback" class="challenge-feedback" aria-live="polite"></output>
    `;
    this.options.panel
      .querySelector("[data-action='close']")
      ?.addEventListener("click", () => this.close());
    this.options.panel
      .querySelector("[data-action='hint']")
      ?.addEventListener("click", () => this.showHint());
    if (challenge.id === "coast.backbone-beach") this.renderBackboneSort();
    else if (challenge.id === "coast.armour-pools") this.renderArmourSort();
    else if (challenge.id === "coast.five-families") this.renderFiveGroups();
    else if (challenge.id === "coast.lighthouse-key") this.renderKeyStart();
    else this.renderKeyRepair();
    shuffleAnswerPresentation(this.options.panel);
    this.options.panel
      .querySelector<HTMLButtonElement>("[data-action='close']")
      ?.focus();
  }

  close(): void {
    this.options.panel.hidden = true;
    this.options.panel.innerHTML = "";
    this.options.onClose();
  }

  private body(): HTMLElement {
    const body =
      this.options.panel.querySelector<HTMLElement>("#challenge-body");
    if (!body) throw new Error("Challenge body missing");
    return body;
  }

  private animal(id: string): AnimalEvidence {
    const animal = classificationAnimals.find(
      (candidate) => candidate.id === id,
    );
    if (!animal) throw new Error(`Unknown classification animal: ${id}`);
    return animal;
  }

  private evidenceCards(
    ids: string[],
    selectName: string,
    options: Array<[string, string]>,
  ): string {
    return `<div class="animal-evidence-grid">${ids
      .map((id) => {
        const animal = this.animal(id);
        return `<article class="animal-evidence-card">
          <span class="animal-icon" aria-hidden="true">${animalIcons[id]}</span>
          <h3>${animal.name}</h3><p>${animal.disclosedEvidence}</p>
          <label>Choose a group<select name="${selectName}" data-animal="${id}"><option value="">Choose</option>${options.map(([value, label]) => `<option value="${value}">${label}</option>`).join("")}</select></label>
        </article>`;
      })
      .join("")}</div>`;
  }

  private bindSort(expected: Record<string, string>, error: string): void {
    this.options.panel
      .querySelector<HTMLFormElement>("#classification-form")
      ?.addEventListener("submit", (event) => {
        event.preventDefault();
        const selections = [
          ...this.options.panel.querySelectorAll<HTMLSelectElement>(
            "select[data-animal]",
          ),
        ];
        const wrong = selections.find(
          (select) => select.value !== expected[select.dataset.animal ?? ""],
        );
        if (wrong) {
          const animal = this.animal(wrong.dataset.animal ?? "");
          this.feedback(
            `${animal.name}: ${error} Read its evidence card and try again.`,
          );
          wrong.focus();
          return;
        }
        this.finish();
      });
  }

  private renderBackboneSort(): void {
    const ids = ["salmon", "puffin", "seal", "crab", "octopus", "earthworm"];
    this.body().innerHTML = `<p>Observe visitors from water, shore, and land. Sort from the written backbone evidence—not where an animal lives or how many legs it has.</p><form id="classification-form">${this.evidenceCards(
      ids,
      "backbone-group",
      [
        ["vertebrate", "Vertebrate—with a backbone"],
        ["invertebrate", "Invertebrate—without a backbone"],
      ],
    )}<button class="primary-button" type="submit">Submit answers</button></form>`;
    this.bindSort(
      Object.fromEntries(
        ids.map((id) => [
          id,
          this.animal(id).hasBackbone ? "vertebrate" : "invertebrate",
        ]),
      ),
      "a vertebrate has a backbone; an invertebrate does not.",
    );
  }

  private renderArmourSort(): void {
    const ids = ["crab", "ladybird", "earthworm", "octopus"];
    this.body().innerHTML = `<p>All four visitors are invertebrates. Decide which have an exoskeleton: a hard outer covering that supports and protects.</p><form id="classification-form">${this.evidenceCards(
      ids,
      "armour-group",
      [
        ["exo", "Has an exoskeleton"],
        ["no-exo", "No exoskeleton"],
      ],
    )}<button class="primary-button" type="submit">Submit answers</button></form>`;
    this.bindSort(
      Object.fromEntries(
        ids.map((id) => [
          id,
          this.animal(id).hasHardOuterCovering ? "exo" : "no-exo",
        ]),
      ),
      "look specifically for a hard outer covering, not just the absence of a backbone.",
    );
  }

  private renderFiveGroups(): void {
    const ids = ["salmon", "frog", "gecko", "puffin", "seal"];
    const options: Array<[string, string]> = [
      ["fish", "Fish"],
      ["amphibian", "Amphibian"],
      ["reptile", "Reptile"],
      ["bird", "Bird"],
      ["mammal", "Mammal"],
    ];
    this.body().innerHTML = `<p>These five animals all have backbones. Use each written trait card to place one in every school-level vertebrate group.</p><aside class="simplified-note"><strong>A useful simplified key</strong> Scientists can use more evidence for unusual animals; here, use the disclosed traits rather than colour or habitat alone.</aside><form id="classification-form">${this.evidenceCards(ids, "vertebrate-group", options)}<button class="primary-button" type="submit">Submit answers</button></form>`;
    this.bindSort(
      Object.fromEntries(ids.map((id) => [id, this.animal(id).outcome])),
      "compare its fins, skin, scales, feathers, or fur with the labelled group traits.",
    );
  }

  private renderKeyStart(): void {
    const required = [
      "salmon",
      "frog",
      "gecko",
      "puffin",
      "seal",
      "crab",
      "octopus",
    ];
    this.body().innerHTML = `<p>Follow every route in the complete yes-or-no key. Start at question 1 for each visitor. Every question and branch is labelled in words.</p><div class="key-map" aria-label="Complete identification key"><strong>Question route</strong><ol><li>Backbone?</li><li>No → hard outer covering? Yes: exoskeleton / No: no exoskeleton</li><li>Yes → feathers? fur? fins and gills? smooth moist skin? dry scales?</li></ol></div><label class="unit-choice">Choose a visitor<select id="key-animal">${required.map((id) => `<option value="${id}">${this.animal(id).name}: ${this.animal(id).disclosedEvidence}</option>`).join("")}</select></label><button class="primary-button" id="start-key" type="button">Start at question 1</button><div id="key-progress" class="key-progress" aria-live="polite"></div>`;
    shuffleAnswerPresentation(this.body());
    this.options.panel
      .querySelector("#start-key")
      ?.addEventListener("click", () => this.startKey());
    this.renderKeyProgress();
  }

  private startKey(): void {
    const id =
      this.options.panel.querySelector<HTMLSelectElement>("#key-animal")
        ?.value ?? "salmon";
    this.keyAnimal = this.animal(id);
    this.keyNode = "backbone";
    this.renderKeyQuestion();
  }

  private renderKeyQuestion(): void {
    const animal = this.keyAnimal;
    if (!animal) return;
    const questions: Record<string, string> = {
      backbone: "Does this animal have a backbone?",
      exoskeleton:
        "Does it have a hard outer covering that supports and protects?",
      feathers: "Does it have feathers?",
      fur: "Does it have fur or hair?",
      fins: "Does it have fins and gills?",
      moist: "Does it have smooth, moist skin?",
      scales: "Does it have dry scales?",
    };
    const progress =
      this.options.panel.querySelector<HTMLElement>("#key-progress");
    if (!progress) return;
    progress.innerHTML = `<section class="key-question"><span class="pill">${animal.name}</span><p><strong>${questions[this.keyNode]}</strong></p><div class="yes-no-controls"><button type="button" data-key-answer="yes">Yes</button><button type="button" data-key-answer="no">No</button></div></section>`;
    shuffleAnswerPresentation(progress);
    for (const button of progress.querySelectorAll<HTMLButtonElement>(
      "[data-key-answer]",
    )) {
      button.addEventListener("click", () =>
        this.followKey(button.dataset.keyAnswer === "yes"),
      );
    }
    progress.querySelector<HTMLButtonElement>("button")?.focus();
  }

  private followKey(answer: boolean): void {
    const transitions: Record<
      string,
      [KeyOutcome | string, KeyOutcome | string]
    > = {
      backbone: ["feathers", "exoskeleton"],
      exoskeleton: [
        "invertebrate-with-exoskeleton",
        "invertebrate-without-exoskeleton",
      ],
      feathers: ["bird", "fur"],
      fur: ["mammal", "fins"],
      fins: ["fish", "moist"],
      moist: ["amphibian", "scales"],
      scales: ["reptile", "invertebrate-without-exoskeleton"],
    };
    const next = transitions[this.keyNode]?.[answer ? 0 : 1];
    if (!next) return;
    if (
      [
        "backbone",
        "exoskeleton",
        "feathers",
        "fur",
        "fins",
        "moist",
        "scales",
      ].includes(next)
    ) {
      this.keyNode = next;
      this.renderKeyQuestion();
      return;
    }
    const animal = this.keyAnimal;
    if (!animal) return;
    const expected = resolveIdentificationKey(animal);
    if (next !== expected) {
      this.feedback(
        `That route ended at ${this.outcomeLabel(next as KeyOutcome)}, but ${animal.name}'s card supports ${this.outcomeLabel(expected)}. Start again and follow its written evidence.`,
      );
      this.startKey();
      return;
    }
    this.completedKeyAnimals.add(animal.id);
    this.feedback(
      `${animal.name} reaches ${this.outcomeLabel(expected)} through an accurate yes-or-no route.`,
      true,
    );
    if (
      ["salmon", "frog", "gecko", "puffin", "seal", "crab", "octopus"].every(
        (id) => this.completedKeyAnimals.has(id),
      )
    ) {
      this.finish();
      return;
    }
    this.renderKeyStart();
  }

  private renderKeyProgress(): void {
    const progress =
      this.options.panel.querySelector<HTMLElement>("#key-progress");
    if (!progress) return;
    progress.textContent = `${this.completedKeyAnimals.size} of 7 labelled routes completed: five vertebrate groups and two invertebrate groups.`;
  }

  private renderKeyRepair(): void {
    const options = (items: Array<[string, string]>) =>
      `<option value="">Choose a question</option>${items.map(([value, label]) => `<option value="${value}">${label}</option>`).join("")}`;
    const broad: Array<[string, string]> = [
      ["backbone", "Does it have a backbone?"],
      ["water", "Does it live in water?"],
      ["legs", "Does it have four legs?"],
    ];
    const noBackbone: Array<[string, string]> = [
      ["hard-covering", "Does it have a hard outer covering?"],
      ["feathers", "Does it have feathers?"],
      ["fur", "Does it have fur?"],
    ];
    const vertebrate: Array<[string, string]> = [
      ["covering", "What covering or body trait can we observe?"],
      ["colour", "What colour is it?"],
      ["home", "Where is its favourite home?"],
    ];
    this.body().innerHTML = `<p>Storm winds removed three question signs. Repair the branches, then use written evidence for an unfamiliar visitor.</p><form id="repair-form" class="repair-form"><label>Question 1—the broad split<select id="repair-first">${options(broad)}</select></label><label>If the answer is “no backbone”<select id="repair-invertebrate">${options(noBackbone)}</select></label><label>If the answer is “has a backbone”<select id="repair-vertebrate">${options(vertebrate)}</select></label><article class="animal-evidence-card animal-evidence-card--transfer"><span class="animal-icon" aria-hidden="true">${animalIcons.newt}</span><h3>Unfamiliar visitor: crested newt</h3><p>${this.animal("newt").disclosedEvidence}</p><label>Where does the repaired key lead?<select id="newt-group"><option value="">Choose</option><option value="fish">Fish</option><option value="amphibian">Amphibian</option><option value="reptile">Reptile</option><option value="bird">Bird</option><option value="mammal">Mammal</option></select></label></article><label>Earlier-region retrieval: what does a contracting muscle pull?<select id="repair-retrieval"><option value="">Choose</option><option value="bones">Bones</option><option value="feathers">Feathers</option><option value="water">Water</option></select></label><button class="primary-button" type="submit">Submit answers</button></form>`;
    this.options.panel
      .querySelector<HTMLFormElement>("#repair-form")
      ?.addEventListener("submit", (event) => {
        event.preventDefault();
        const values = [
          this.options.panel.querySelector<HTMLSelectElement>("#repair-first")
            ?.value,
          this.options.panel.querySelector<HTMLSelectElement>(
            "#repair-invertebrate",
          )?.value,
          this.options.panel.querySelector<HTMLSelectElement>(
            "#repair-vertebrate",
          )?.value,
          this.options.panel.querySelector<HTMLSelectElement>("#newt-group")
            ?.value,
          this.options.panel.querySelector<HTMLSelectElement>(
            "#repair-retrieval",
          )?.value,
        ];
        const expected = [
          "backbone",
          "hard-covering",
          "covering",
          "amphibian",
          "bones",
        ];
        const messages = [
          "Begin with the broad backbone question so both major routes work.",
          "The no-backbone branch should ask about a hard outer covering to distinguish exoskeletons.",
          "The vertebrate branch needs observable body traits, not colour or favourite habitat.",
          "Use the newt's disclosed smooth, moist skin: this simplified key leads to amphibian.",
          "Earlier regions showed that contracting muscles pull bones.",
        ];
        const wrong = values.findIndex(
          (value, index) => value !== expected[index],
        );
        if (wrong >= 0) {
          this.feedback(
            messages[wrong] ?? "Repair this key step and try again.",
          );
          return;
        }
        this.finish();
      });
  }

  private outcomeLabel(outcome: KeyOutcome): string {
    const labels: Record<KeyOutcome, string> = {
      fish: "fish",
      amphibian: "amphibian",
      reptile: "reptile",
      bird: "bird",
      mammal: "mammal",
      "invertebrate-with-exoskeleton": "invertebrate with an exoskeleton",
      "invertebrate-without-exoskeleton": "invertebrate without an exoskeleton",
    };
    return labels[outcome];
  }

  private feedback(message: string, success = false): void {
    const output = this.options.panel.querySelector<HTMLOutputElement>(
      "#challenge-feedback",
    );
    if (!output) return;
    output.textContent = message;
    output.classList.toggle("challenge-feedback--success", success);
    if (success || this.hintsUsed > 0) this.options.speak(message);
  }

  private showHint(): void {
    const challenge = this.challenge;
    if (!challenge) return;
    this.hintsUsed = Math.min(2, this.hintsUsed + 1);
    const hint = challenge.hints[this.hintsUsed - 1] ?? challenge.hints[1];
    const text = this.options.panel.querySelector<HTMLElement>("#hint-text");
    if (text) text.textContent = hint;
    this.options.speak(hint);
    const button = this.options.panel.querySelector<HTMLButtonElement>(
      "[data-action='hint']",
    );
    if (button) {
      button.textContent =
        this.hintsUsed === 1 ? "Show a bigger hint" : "Hint shown";
      button.disabled = this.hintsUsed === 2;
    }
  }

  private finish(): void {
    const challenge = this.challenge;
    if (!challenge || this.body().querySelector(".finish-button")) return;
    this.feedback(challenge.successExplanation, true);
    const button = document.createElement("button");
    button.type = "button";
    button.className = "primary-button finish-button";
    button.textContent = "Relight this Habitat Beacon";
    button.addEventListener("click", () => {
      this.options.onComplete(challenge, this.hintsUsed);
      this.close();
    });
    this.body().append(button);
    button.focus();
  }
}
